<template>
	<loading-splash v-if="showLoadingSplash" />

	<event-filters ref="eventFilters" :availablePostTypes="settings.post_types"
		:availableTaxonomyTerms="settings.taxonomy_terms" :defaultQuery="defaultQuery" @update-filters="updateFilters"
		@toggle-map="toggleMap" />

	<div ref="eventsContainer" class="events">
		<template v-if="enableMap">
			<div class="row">
				<div class="col">
					<events-map ref="eventsMap" class="vue-events-plugin-map" :mapDefaultProximity="mapDefaultProximity"
						:mapboxKey="mapMapboxKey" :query="query"></events-map>
				</div>
			</div>
		</template>
		<div class="row g-4">
			<event-card class="event" v-for="event in events" :model="event" :key="event.ID" />
		</div>
		<div class="row" v-if="totalPages > 1">
			<div class="col">
				<event-pagination ref="pagination" :useInfiniteScroll="useInfiniteScroll"
					:currentPage="query['events-page']" :totalPages="totalPages" :loading="loading"
					@page-changed="changePage" @page-append="addPage">
				</event-pagination>
			</div>
		</div>
	</div>

	<error-splash v-if="error" :message="error.statusText" :status="error.status" />

</template>

<script>
import { defineAsyncComponent } from 'vue'

import { getList } from '@/services/EventsGetter'
import EventFilters from '@/components/EventFilters.vue';
import EventCard from '@/components/EventCard.vue';
import LoadingSplash from '@/components/LoadingSplash.vue';
import ErrorSplash from '@/components/ErrorSplash.vue';
import EventPagination from '@/components/EventPagination.vue';

export default {
	name: 'EventsList',
	components: {
		EventFilters,
		EventCard,
		LoadingSplash,
		ErrorSplash,
		EventPagination,
		EventsMap: defineAsyncComponent(() => import(/* webpackChunkName: "EventsMap" */ '@/components/EventsMap.vue'))
	},
	props: {
		class: String
	},
	inject: [
		'settings'
	],
	data() {
		return {
			loadUpToPrevInfScrllPage: false,
			isFirstPaint: true,
			loading: false,
			showLoadingSplash: false,
			events: [],
			totalPages: 1,
			error: null,
			defaultQuery: {
				'per-page': 15,
			},
			query: {
				'events-page': parseInt(this.$route.query['events-page']) || 1,
				'per-page': 15,
				'post-types[]': []
			},
			enableMap: ('1' === process.env.VUE_APP_SHOW_MAP || false),
			useScrollToTop: ('1' === process.env.VUE_APP_USE_SCROLL_TO_TOP_ON_CHANGE || false),
			mapDefaultProximity: process.env.VUE_APP_DEFAULT_PROXIMITY || "-2.5861567075381906,51.46088142611168",
			mapMapboxKey: process.env.VUE_APP_MAPBOX_KEY || null,

		}
	},
	computed: {
		useInfiniteScroll() {
			if ('undefined' !== typeof this.$route.query['inf-scroll']) {
				return (this.$route.query['inf-scroll'])
			}
			return ('1' === process.env.VUE_APP_USE_INFINITE_SCROLL || false) ? true : false
		}
	},
	methods: {
		toggleMap() {
			if (this.$refs.eventsMap) {
				this.$refs.eventsMap.toggleMap()
			}
		},
		async reset() {
			this.error = null
			this.events = []
			this.totalPages = 1
		},
		async loadEvents(append = false) {
			// Do nothing if already loading
			if (this.loading) {
				return
			}

			const theQuery = { ...this.query }

			// Set loading state
			this.loading = true

			if (false === append || !this.useInfiniteScroll) {
				this.showLoadingSplash = true
			}

			// Reset to empty old event data
			if (!append) {
				await this.reset()
			}

			// Hold original per_page
			const originalPerPage = theQuery['per-page'] || 1

			// Decide whether to run first paint load
			const firstPaint = (this.isFirstPaint) ? true : false
			const doFirstPaintLoad = (firstPaint && this.useInfiniteScroll) ? true : false

			// Disable first paint load
			this.isFirstPaint = false

			// Load more results if returning to an infinite scroll
			if (this.loadUpToPrevInfScrllPage && doFirstPaintLoad) {
				theQuery['per-page'] = Math.ceil(theQuery['events-page'] * originalPerPage)
				theQuery['events-page'] = 1
			}

			// Make a new REST request
			const response = await getList(theQuery)

			// Stop on error
			if (200 === response.status) {
				// Update class event data
				this.events = this.events.concat(response.data)
				this.totalPages = Math.ceil(response.totalResults / originalPerPage)
			} else {
				// Show error splash
				this.error = response
				this.events = []
			}

			// Disable loading splash
			this.loading = false
			this.showLoadingSplash = false

			// Scroll to top of events
			if (this.useScrollToTop && !append && !firstPaint && !this.useInfiniteScroll) {
				this.scrollToRef('eventsContainer')
			}

			console.log('useScrollToTop', this.useScrollToTop)
		},
		async updateFilters(filters, append = true) {
			// Do nothing if already loading
			if (this.loading) {
				return
			}

			const originalQuery = (append) ? this.query : {}

			this.query = {
				...originalQuery,
				...filters,
				'events-page': 1
			}

			this.queryParamPush(this.query, null, false)

			await this.loadEvents()

			if (this.$refs.eventsMap) {
				this.$refs.eventsMap.updateEvents()
			}
		},
		scrollToRef(refName) {
			const element = this.$refs[refName]
			if (element) {
				const top = element.offsetTop
				window.scrollTo(0, top)
			}
		},
		async addPage(number) {
			// Do nothing if already loading
			if (this.loading) {
				return
			}

			this.query['events-page'] = parseInt(number)
			this.queryParamPush('events-page', this.query['events-page'])
			await this.loadEvents(true)
		},
		async changePage(number) {
			// Do nothing if already loading
			if (this.loading) {
				return
			}

			this.query['events-page'] = parseInt(number)
			this.queryParamPush('events-page', this.query['events-page'])
			await this.loadEvents()
		},
		queryParamPush(paramName, value, append = true) {
			let pushQuery = {}
			const originalQuery = (append) ? this.$route.query : {}

			if ('object' == typeof paramName && !value) {
				pushQuery = {
					...originalQuery,
					...paramName,
				}
			} else {
				pushQuery = {
					...originalQuery,
					[paramName]: value,
				}

				if (!value) {
					delete pushQuery[paramName]
				}
			}

			if (pushQuery['per-page']) {
				delete pushQuery['per-page']
			}

			this.$router.push({
				path: this.$route.path,
				query: pushQuery,
			});
		},
	},
	async mounted() {
		if (this.$refs.eventFilters) {
			this.$refs.eventFilters.triggerFilterReload(false)
		} else {
			await this.loadEvents(true)
		}
	},
}
</script>

<style lang="scss"></style>
